import classNames from 'classnames';
import React from 'react';
import type { ColorId } from 'unsplash-js';
import Button from './Button';
import SVGIcon from './SVGIcon';
import * as styles from './UnsplashColorPicker.module.scss';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';
import { calculateLuma, rgbColorToString } from '../utils/colors';
import type { RGBColor } from '../utils/colors';

export type SupportedColorId = Exclude<ColorId, 'teal' | 'black_and_white'>;

const COLORS: Record<SupportedColorId, RGBColor> = {
  black: [0, 0, 0],
  white: [255, 255, 255],
  yellow: [252, 229, 21],
  red: [221, 9, 9],
  purple: [115, 44, 197],
  magenta: [255, 0, 255],
  green: [55, 100, 45],
  blue: [79, 164, 234],
  orange: [234, 115, 46],
};

const LUMA_THRESHOLD = 0.5;

interface UnsplashColorProps {
  color?: SupportedColorId | null;
  selected?: boolean;
  textColor?: 'black' | 'white';
}

export const UnsplashColor = ({
  color,
  selected,
  textColor,
}: UnsplashColorProps) => {
  return (
    <div
      className={classNames(styles.color, {
        [styles.defaultColor]: color == null,
      })}
      style={
        color
          ? {
              backgroundColor: rgbColorToString(COLORS[color]),
              color: textColor,
            }
          : undefined
      }
    >
      {selected && <SVGIcon>{checkIcon}</SVGIcon>}
    </div>
  );
};

interface UnsplashColorsProps {
  value: SupportedColorId | null;
  onSelect(id: SupportedColorId | null): void;
}

const UnsplashColors = ({ onSelect, value }: UnsplashColorsProps) => {
  return (
    <div className={styles.colors}>
      <span
        style={
          {
            '--ring-color': rgbColorToString(COLORS.white, 0.2),
          } as React.CSSProperties
        }
      >
        <Button
          className={classNames(styles.colorButton, styles.defaultColor, {
            [styles.isSelected]: value == null,
          })}
          onClick={() => onSelect(null)}
        >
          <UnsplashColor />
        </Button>
      </span>
      {Object.entries(COLORS).map(([key, color]) => {
        const colorId = key as SupportedColorId;
        const isSelected = value === colorId;
        return (
          <span
            key={colorId}
            style={
              {
                '--ring-color': rgbColorToString(color, 0.2),
              } as React.CSSProperties
            }
          >
            <Button
              className={classNames(styles.colorButton, styles[colorId], {
                [styles.isSelected]: isSelected,
              })}
              onClick={() => onSelect(colorId)}
            >
              <UnsplashColor
                color={colorId}
                selected={isSelected}
                textColor={
                  calculateLuma(color) <= LUMA_THRESHOLD ? 'white' : 'black'
                }
              />
            </Button>
          </span>
        );
      })}
    </div>
  );
};

export default UnsplashColors;
